import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    IconButton,
    Tooltip,
    Skeleton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getFactorRatings } from "../../../api/stocks_api";
import { getRecentlyViewed } from "../../../api/api";
import RecentlyViewedWidget from "../Widgets/recentlyViewedBox";
import { Info, ChevronDown } from "lucide-react";
import { fmtL } from "../../../utils/currency";
import {
    BALANCE_SHEET_FACTOR,
    normaliseBalanceSheetBreakdown,
} from "../../../config/factors";
/* Dated factor-ratings series from GET /stocks/{ticker}/valuation-history */
import { RatingsHistory } from "../Widgets/valuationHistory";

/* -------------------- GRADE COLOR MAP -------------------- */
const gradeBackground = (grade) => {
    const map = {
        "A+": "var(--color-grade-a-plus)",
        "A": "var(--color-grade-a)",
        "A-": "var(--color-grade-a-minus)",

        "B+": "var(--color-grade-b-plus)",
        "B": "var(--color-grade-b)",
        "B-": "var(--color-grade-b-minus)",

        "C+": "var(--color-grade-c-plus)",
        "C": "var(--color-grade-c)",
        "C-": "var(--color-grade-c-minus)",

        "D+": "var(--color-grade-d-plus)",
        "D": "var(--color-grade-d)",
        "D-": "var(--color-grade-d-minus)",

        "E": "var(--color-grade-e)",

        "F": "var(--color-grade-f)",
    };

    return map[grade] ?? "var(--color-grade-unknown)";
};

/* -------------------- TABLE SKELETON -------------------- */
const TableSkeleton = () => (
    <Box sx={{ p: 3 }}>
        {[...Array(5)].map((_, idx) => (
            <Box key={idx} sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                pb: 2,
                borderBottom: idx < 4 ? "1px solid var(--color-border-primary)" : "none",
            }}>
                <Skeleton variant="text" width="40%" height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": { background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)" },
                }} />
                <Skeleton variant="rounded" width={80} height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": { background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)" },
                }} />
            </Box>
        ))}
    </Box>
);

const Ratings = ({ stock }) => {
    const { ticker } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [factorRatings, setFactorRatings] = useState(null);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    /* ── Balance-Sheet Quality: lazily-loaded breakdown for the 6th factor ── */
    const [bsOpen, setBsOpen] = useState(false);
    const [bsLoading, setBsLoading] = useState(false);
    const [bsBreakdown, setBsBreakdown] = useState([]);
    const [bsFetched, setBsFetched] = useState(false);

    useEffect(() => {
        if (!ticker) return;

        const loadFactorRatings = async () => {
            setLoading(true);
            try {
                const res = await getFactorRatings(ticker, "overall");

                if (res?.authExpired) {
                    navigate("/login");
                    return;
                }

                if (res?.success && res?.response_data && res.response_data.overall_grade != null) {
                    setFactorRatings(res.response_data);
                } else {
                    setFactorRatings(null);
                }
            } catch (err) {
                console.error("FA Ratings fetch error:", err);
                setFactorRatings(null);
            }
            setLoading(false);
        };

        loadFactorRatings();
    }, [ticker, navigate]);

    /* Reset the breakdown whenever the ticker changes */
    useEffect(() => {
        setBsOpen(false);
        setBsFetched(false);
        setBsBreakdown([]);
    }, [ticker]);

    /* -------------------- BALANCE-SHEET QUALITY BREAKDOWN -------------------- */
    /* Fetched only on first expand — keeps the tab to a single request by default. */
    const toggleBalanceSheet = async () => {
        const opening = !bsOpen;
        setBsOpen(opening);
        if (!opening || bsFetched || !ticker) return;

        setBsLoading(true);
        try {
            const res = await getFactorRatings(ticker, BALANCE_SHEET_FACTOR.param);

            if (res?.authExpired) {
                navigate("/login");
                return;
            }

            setBsBreakdown(
                res?.success ? normaliseBalanceSheetBreakdown(res.response_data?.breakdown) : []
            );
            setBsFetched(true);
        } catch (err) {
            console.error("Balance-Sheet Quality breakdown fetch error:", err);
            setBsBreakdown([]);
        }
        setBsLoading(false);
    };

    /* -------------------- RECENTLY VIEWED -------------------- */
    useEffect(() => {
        const fetchRecentlyViewed = async () => {
            setLoadingRecent(true);
            const res = await getRecentlyViewed();

            if (res.success) {
                setRecentlyViewed(
                    res.data.filter((item) => item.ticker !== stock?.ticker)
                );
            }
            setLoadingRecent(false);
        };

        if (stock?.ticker) fetchRecentlyViewed();
    }, [stock?.ticker]);

    /* -------------------- LOADING STATE -------------------- */
    if (loading) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Paper elevation={0} sx={{
                        bgcolor: "var(--color-surface)",
                        border: "1px solid var(--color-border-primary)",
                        borderRadius: "var(--radius-xl)",
                        overflow: "hidden",
                        boxShadow: "var(--shadow-md)",
                    }}>
                        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2, borderBottom: "1px solid var(--color-border-primary)" }}>
                            <Skeleton variant="text" width={200} height={32} sx={{ mb: 1, bgcolor: "var(--color-surface-hover)" }} />
                            <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: "var(--color-surface-hover)" }} />
                        </Box>
                        <TableSkeleton />
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                    <RecentlyViewedWidget data={[]} loading={true} />
                </Box>
            </Box>
        );
    }

    if (!factorRatings) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2, flexDirection: { xs: "column", lg: "row" } }}>
                <Box sx={{ flex: 1 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            border: "1px solid var(--color-border-primary)",
                            borderRadius: "var(--radius-xl)",
                            overflow: "hidden",
                            boxShadow: "var(--shadow-md)",
                            p: { xs: 3, sm: 4 },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 200,
                            gap: 2,
                        }}
                    >
                        <Info size={40} style={{ color: "var(--color-text-muted)" }} />
                        <Typography sx={{ fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--color-text-primary)" }}>
                            Data not available for this ticker
                        </Typography>
                        <Typography sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", textAlign: "center", maxWidth: 400 }}>
                            FA Ratings data is not available for {ticker}. This stock may not have sufficient data for factor analysis.
                        </Typography>
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                    <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
                </Box>
            </Box>
        );
    }

    /* ── SIX factors. "Risk" is now PRICE risk; Balance-Sheet Quality is the
          factor split out of it (grade shows "—" until the backend pushes it). ── */
    const factorRows = [
        { label: "Valuation", grade: factorRatings?.valuation_grade },
        { label: "Growth", grade: factorRatings?.growth_grade },
        { label: "Momentum", grade: factorRatings?.momentum_grade },
        { label: "Financial Profitability", grade: factorRatings?.financial_profit_grade },
        { label: "Risk", grade: factorRatings?.risk_grade },
        {
            label: BALANCE_SHEET_FACTOR.fullLabel,
            grade: factorRatings?.[BALANCE_SHEET_FACTOR.gradeKey],
            expandable: true,
        },
    ];

    return (
        <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Paper elevation={0} sx={{
                    bgcolor: "var(--color-surface)",
                    border: "1px solid var(--color-border-primary)",
                    borderRadius: "var(--radius-xl)",
                    overflow: "hidden",
                }}>

                    {/* ── HEADER — mirrors Profitability.jsx layout ── */}
                    <Box sx={{
                        px: 3,
                        py: 3,
                        borderBottom: "1px solid var(--color-border-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        {/* Left: Title + Rating Date */}
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "1.3rem",
                                    fontWeight: 600,
                                    color: "var(--color-text-primary)",
                                }}
                            >
                                {ticker} FA Ratings
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.9rem",
                                    color: "var(--color-text-secondary)",
                                    mt: 0.5,
                                }}
                            >
                                FA Rating: {factorRatings?.overall_rating}
                            </Typography>
                            {/* <Typography sx={{ typography: "subtitle", color: "var(--color-text-muted)", mt: 0.5 }}>
                                Rating Date: {factorRatings?.rating_date?.split("T")[0]}
                            </Typography> */}
                        </Box>

                        {/* Right: Overall Grade chip + Overall Score */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{
                                display: "inline-flex",
                                px: 3,
                                py: 1,
                                borderRadius: "var(--radius-md)",
                                bgcolor: gradeBackground(factorRatings?.overall_grade),
                            }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        color: "#ffffff",
                                    }}
                                >
                                    {factorRatings?.overall_grade ?? "—"}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: "var(--color-primary)",
                                }}>
                                FA Score: {factorRatings?.overall_score != null ? fmtL(factorRatings.overall_score, 1) : "—"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* ── FACTOR TABLE ── */}
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                        <Box component="thead">
                            <Box component="tr">
                                {["Factor", "FA Grade"].map((header) => (
                                    <Box key={header} component="th" sx={{
                                        textAlign: "left",
                                        p: 2,
                                        typography: "tableHeader",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        color: "var(--color-text-muted)",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                    }}>
                                        {header}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box component="tbody">
                            {factorRows.map((row) => (
                                <React.Fragment key={row.label}>
                                    <Box component="tr" sx={{
                                        "&:hover": { bgcolor: "var(--color-surface-hover)" },
                                        transition: "background-color var(--transition-fast)",
                                        cursor: row.expandable ? "pointer" : "default",
                                    }}
                                        onClick={row.expandable ? toggleBalanceSheet : undefined}
                                    >
                                        {/* Factor name */}
                                        <Box component="td" sx={{
                                            p: 2,
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            typography: "paragraph",
                                            fontWeight: "var(--font-weight-medium)",
                                            color: "var(--color-text-primary)",
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {row.label}
                                                {row.expandable && (
                                                    <Tooltip title={bsOpen ? "Hide breakdown" : "Show breakdown"}>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={bsOpen ? "Hide Balance-Sheet Quality breakdown" : "Show Balance-Sheet Quality breakdown"}
                                                            aria-expanded={bsOpen}
                                                            onClick={(e) => { e.stopPropagation(); toggleBalanceSheet(); }}
                                                            sx={{ p: 0.25, color: "var(--color-text-muted)" }}
                                                        >
                                                            <ChevronDown
                                                                size={16}
                                                                style={{
                                                                    transform: bsOpen ? "rotate(180deg)" : "none",
                                                                    transition: "transform var(--transition-fast)",
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Grade chip */}
                                        <Box component="td" sx={{
                                            p: 2,
                                            borderBottom: "1px solid var(--color-border-primary)",
                                        }}>
                                            <Box sx={{
                                                display: "inline-flex",
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: "var(--radius-sm)",
                                                bgcolor: gradeBackground(row.grade),
                                            }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "#ffffff",
                                                    }}
                                                >
                                                    {row.grade ?? "—"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* ── BALANCE-SHEET QUALITY BREAKDOWN (expandable) ── */}
                                    {row.expandable && bsOpen && (
                                        <Box component="tr">
                                            <Box component="td" colSpan={2} sx={{
                                                p: 0,
                                                borderBottom: "1px solid var(--color-border-primary)",
                                                bgcolor: "var(--color-bg-tertiary)",
                                            }}>
                                                <Box sx={{ px: 3, py: 2 }}>
                                                    {bsLoading ? (
                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                            {[...Array(4)].map((_, i) => (
                                                                <Skeleton key={i} variant="text" width="55%" height={22}
                                                                    sx={{ bgcolor: "var(--color-surface-hover)" }} />
                                                            ))}
                                                        </Box>
                                                    ) : bsBreakdown.length === 0 ? (
                                                        <Typography sx={{
                                                            fontSize: "var(--font-size-sm)",
                                                            color: "var(--color-text-muted)",
                                                        }}>
                                                            Breakdown not available for {ticker}.
                                                        </Typography>
                                                    ) : (
                                                        <>
                                                            <Typography sx={{
                                                                fontSize: "var(--font-size-xs)",
                                                                fontWeight: 700,
                                                                letterSpacing: "0.05em",
                                                                textTransform: "uppercase",
                                                                color: "var(--color-text-muted)",
                                                                mb: 1.5,
                                                            }}>
                                                                {BALANCE_SHEET_FACTOR.fullLabel} Breakdown
                                                            </Typography>

                                                            <Box sx={{
                                                                display: "grid",
                                                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                gap: 1.25,
                                                            }}>
                                                                {bsBreakdown.map((item) => (
                                                                    <Box key={item.key} sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "space-between",
                                                                        gap: 2,
                                                                        px: 1.5,
                                                                        py: 1,
                                                                        borderRadius: "var(--radius-sm)",
                                                                        bgcolor: "var(--color-surface)",
                                                                        border: "1px solid var(--color-border-primary)",
                                                                    }}>
                                                                        <Typography sx={{
                                                                            fontSize: "var(--font-size-sm)",
                                                                            color: "var(--color-text-secondary)",
                                                                        }}>
                                                                            {item.label}
                                                                        </Typography>

                                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                            <Typography sx={{
                                                                                fontSize: "var(--font-size-sm)",
                                                                                fontWeight: 600,
                                                                                color: "var(--color-text-primary)",
                                                                            }}>
                                                                                {item.value != null ? fmtL(item.value, 2) : "—"}
                                                                            </Typography>

                                                                            {item.grade && (
                                                                                <Box sx={{
                                                                                    display: "inline-flex",
                                                                                    minWidth: 34,
                                                                                    justifyContent: "center",
                                                                                    px: 1,
                                                                                    py: 0.25,
                                                                                    borderRadius: "var(--radius-sm)",
                                                                                    bgcolor: gradeBackground(item.grade),
                                                                                }}>
                                                                                    <Typography sx={{
                                                                                        fontSize: "var(--font-size-xs)",
                                                                                        fontWeight: 700,
                                                                                        color: "#ffffff",
                                                                                    }}>
                                                                                        {item.grade}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}

                            {/* ── OVERALL ROW ── */}
                            {/* <Box component="tr" sx={{ bgcolor: "var(--color-bg-tertiary)" }}>
                                <Box component="td" sx={{
                                    p: 2,
                                    typography: "paragraph",
                                    fontWeight: "var(--font-weight-bold)",
                                    color: "var(--color-text-primary)",
                                    borderTop: "2px solid var(--color-border-secondary)",
                                }}>
                                    Overall
                                </Box>
                                <Box component="td" sx={{
                                    p: 2,
                                    borderTop: "2px solid var(--color-border-secondary)",
                                }}>
                                    <Box sx={{
                                        display: "inline-flex",
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: "var(--radius-sm)",
                                        bgcolor: gradeBackground(factorRatings?.overall_grade),
                                    }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: "#ffffff",
                                            }}
                                        >
                                            {factorRatings?.overall_grade ?? "—"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box> */}
                        </Box>
                    </Box>
                </Paper>

                {/* ── FACTOR-RATINGS HISTORY ── */}
                <RatingsHistory ticker={ticker} />
            </Box>

            {/* ── RECENTLY VIEWED ── */}
            <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
            </Box>
        </Box>
    );
};

export default Ratings;